import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../utils/UseDebounce";
import { showSuccessToast } from "../../utils/Toast";
import RestoreIcon from "@mui/icons-material/Restore";
import { getAllAdminTeams, deleteTeam, restoreTeam } from "../../service/teamService";

interface Team {
  isDeleted?: boolean;
  teamName: string;
  tenantId: string;
  id: string;
  tenantName: string | null;
  tenantEmail: string | null;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: "bold",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TableWrapper = styled(TableContainer)({
  margin: "auto",
  marginTop: "1%",
  width: "80%",
}) as typeof TableContainer;

export default function ManageTeams() {
  const [teamData, setTeamData] = useState<Team[]>([]);
  const [teamResponse, setTeamResponse] = useState<any>();
  const [id, setId] = useState("");
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [t] = useTranslation("global");
  const [page, setPage] = useState(0); // Page number starts from 0
  const [rowsPerPage, setRowsPerPage] = useState(10); // Rows per page
  const [query] = useState("");
  const debounceQuery = useDebounce(query, 1000);
  const [isRestoreModal, setIsRestoreModal] = useState(false);
  const [restoreTeamId, setRestoreTeamId] = useState("");

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchTeams(newPage, rowsPerPage); 
  };

  useEffect(() => {
    if (debounceQuery || debounceQuery === "") {
      fetchTeams(page, rowsPerPage, debounceQuery);
    }
  }, [page, rowsPerPage, debounceQuery]);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    fetchTeams(0, parseInt(event.target.value, 10)); 
  };

  const handleDeleteClick = (teamId: string) => {
    setId(teamId);
    setIsDeleteModal(true);
  };

  const fetchTeams = async (pageNo: number = 0, limit: number = rowsPerPage, searchQuery?: string) => {
    const fetchedTeams = await getAllAdminTeams(isMobile, navigate, limit, pageNo + 1, searchQuery);
    if (fetchedTeams) {
      setTeamResponse(fetchedTeams);
      setTeamData(fetchedTeams.results);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModal(false);
  };

  const handleDeleteTeam = async () => {
    setIsDeleteModal(false);
    const response = await deleteTeam(id, isMobile, navigate);
    if (response?.status >= 200 && response?.status <= 301) {
      showSuccessToast(t("manageUsers.deleteSuccess") || "Team deleted successfully", isMobile);
    }
    fetchTeams();
  };

  const handleRestoreClick = (teamId: string) => {
    setRestoreTeamId(teamId);
    setIsRestoreModal(true);
  };

  const handleCancelRestore = () => {
    setIsRestoreModal(false);
    setRestoreTeamId("");
  };

  const handleRestoreTeamAction = async () => {
    setIsRestoreModal(false);
    const response = await restoreTeam(restoreTeamId, isMobile, navigate);
    if (response?.status >= 200 && response?.status <= 301) {
      showSuccessToast(t("manageUsers.restoreSuccess") || "Team restored successfully", isMobile);
    }
    fetchTeams();
    setRestoreTeamId("");
  };

  return (
    <Layout>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          marginLeft: "10%",
          marginRight: "10%",
          marginTop: "2%",
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Manage Teams
        </Typography>
      </div>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Dialog open={isDeleteModal} onClose={handleCancelDelete}>
          <DialogTitle>{t("managetenant.t40") || "Delete Team"}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("managetenant.t41") || "Are you sure you want to delete this Team?"}</DialogContentText>
          </DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", m: 2 }}>
            <Button
              onClick={handleCancelDelete}
              color="primary"
              variant="outlined"
              sx={{ mr: 1 }}
            >
              {t("managetenant.t42") || "Cancel"}
            </Button>
            <Button onClick={handleDeleteTeam} color="error" variant="contained">
              {t("managetenant.t43") || "Delete"}
            </Button>
          </Box>
        </Dialog>

        {/* Restore Pop up */}
        <Dialog open={isRestoreModal} onClose={handleCancelRestore}>
          <DialogTitle>{t("managetenant.t48") || "Restore Team"}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("managetenant.t49") || "Are you sure you want to restore this team?"}</DialogContentText>
          </DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", m: 2 }}>
            <Button
              onClick={handleCancelRestore}
              color="primary"
              variant="outlined"
              sx={{ mr: 1 }}
            >
              {t("managetenant.t42") || "Cancel"}
            </Button>
            <Button onClick={handleRestoreTeamAction} color="success" variant="contained">
              {t("managetenant.t39") || "Restore"}
            </Button>
          </Box>
        </Dialog>

        <TableWrapper component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                  Team Name
                </StyledTableCell>
                <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                  Tenant Name
                </StyledTableCell>
                <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                  Tenant Email
                </StyledTableCell>
                <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamData.map((team) => {
                const isTeamDeleted = team.isDeleted;
                return (
                  <StyledTableRow
                    key={team.id}
                    sx={{
                      backgroundColor: isTeamDeleted ? '#ffebee' : 'inherit',
                      '& .MuiTableCell-root': {
                        color: isTeamDeleted ? '#d32f2f' : 'inherit',
                      }
                    }}
                  >
                    <TableCell align="center">{isTeamDeleted ? `${team.teamName} (Deleted)` : team.teamName}</TableCell>
                    <TableCell align="center">{team.tenantName || '-'}</TableCell>
                    <TableCell align="center">{team.tenantEmail || '-'}</TableCell>
                    <TableCell align="center">
                      {isTeamDeleted ? (
                        <>
                          <IconButton
                            aria-label="restore"
                            onClick={() => handleRestoreClick(team.id)}
                            sx={{ color: '#4caf50' }}
                          >
                            <RestoreIcon />
                          </IconButton>
                          <IconButton aria-label="delete" onClick={() => handleDeleteClick(team.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton aria-label="delete" onClick={() => handleDeleteClick(team.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
          {teamResponse && (
            <TablePagination
              component="div"
              count={teamResponse.totalResults || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            />
          )}
        </TableWrapper>
      </Box>
    </Layout>
  );
}
